import { useCss } from '@/composables/useCss.js'
import { useJs } from '@/composables/useJs.js'
import { useCustomerStore } from '@/stores/customer.js'
import { reactive } from 'vue'

const MAX_RETRY_COUNT = 3
const ALLOW_RETRY_ON_STATUS = [504, 509, 598, 599]

const useProps = () => ({
  blockData: String,
  blockCss: String,
  blockJs: String,
  identifier: String,
  v2: {
    type: Boolean,
    default: false
  },
  disableAnimation: {
    type: Boolean,
    default: false
  },
  hideOnError: {
    type: Boolean,
    default: false
  }
})

const useBlock = (props) => {
  const state = reactive({
    id: null,
    value: null,
    js: '',
    css: '',
    displayTool: false,
    maxRetryCount: MAX_RETRY_COUNT,
    retryCount: 0,
    requestFailed: false,
    isV2: false,
    contentType: null,
    componentName: null,
    blockContent: null
  })

  const getBlock = () => {
    _fetch(props, state).catch(() => {})
    useCss(state)
    useJs(state)
  }

  const retry = () => {
    if (state.retryCount > state.maxRetryCount) return

    state.requestFailed = false
    getBlock()
  }

  const toggleAdminTools = async () => {
    const customer = useCustomerStore()
    if (!customer.customerIsAdmin) return

    state.displayTool = !state.displayTool
  }

  if (props.blockData) {
    state.value = props.blockData
    state.js = props.blockJs
    state.css = props.blockCss
  } else {
    getBlock()
  }

  return { retry, state, toggleAdminTools }
}

const _fetch = async (props, state) => {
  const { blocks } = await _getModules()

  try {
    if (props.v2) {
      const block = await blocks.fetchOneV2(props.identifier)
      state.id = block.id
      state.value = block.file
      state.js = block.js
      state.css = block.css
      state.isV2 = true
      state.contentType = block.contentType
      state.componentName = block.componentName
      state.blockContent = block.blockContent
    } else {
      const { js, css, value, id } = await blocks.fetchOne(props.identifier)
      state.id = id
      state.value = value
      state.js = js
      state.css = css
    }
  } catch (e) {
    await _handleError(e, props, state)
  }
}

const _handleError = async (e, { identifier, hideOnError }, state) => {
  console.error(`CMS block fetch failed [${identifier}]:`, e)

  if (hideOnError) return
  _canRetry(e, state)

  const { MessageHelper } = await _getModules()
  MessageHelper.addErrorMessage(
    'cms_loading_error',
    null,
    5000,
    e,
    'cms_block_fetch_failed',
    'notice'
  )
}

const _canRetry = ({ response: { status } = {} }, state) => {
  state.requestFailed = true
  if (!ALLOW_RETRY_ON_STATUS.includes(status)) {
    state.retryCount = MAX_RETRY_COUNT
    return
  }
  state.retryCount++
}

const _getModules = (() => {
  let cache
  return async () => {
    if (cache) return cache
    const [{ useCmsBlocks }, { useCustomerStore }, { default: MessageHelper }] =
      await Promise.all([
        await import('@/stores/cmsBlocks.js'),
        await import('@/stores/customer.js'),
        await import('@/services/helpers/MessageHelper')
      ])
    // Getters, not resolved instances: a cached store instance belongs to
    // whichever pinia was active on first use, which on the server is another
    // request's app (VIP-12037).
    cache = {
      MessageHelper,
      get blocks() {
        return useCmsBlocks()
      },
      get customer() {
        return useCustomerStore()
      }
    }
    return cache
  }
})()

export { useBlock, useProps }
